import { type Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

/**
 * Centralised scenario-change highlight styles.
 *
 * In **dark mode** the theme's `warning.main` (bright yellow) already has good
 * contrast against dark backgrounds. In **light mode** we fall back to
 * `warning.dark` so borders, labels and text remain clearly visible on white.
 */

/** Returns the appropriate warning colour token for the current theme mode. */
export const getScenarioHighlightColor = (theme: Theme): string =>
  theme.palette.mode === 'dark'
    ? theme.palette.warning.main
    : theme.palette.warning.dark;

/**
 * SX object for highlighted form fields (outlined inputs + date-pickers).
 *
 * Apply via `sx={(theme) => (isChanged ? scenarioFieldHighlightSx(theme) : undefined)}`.
 */
export const scenarioFieldHighlightSx = (theme: Theme) => {
  const color = getScenarioHighlightColor(theme);
  return {
    '& .MuiOutlinedInput-root fieldset': {
      borderColor: color,
      borderWidth: 2,
    },
    '& .MuiInputBase-root fieldset': {
      borderColor: color,
      borderWidth: 2,
    },
    '& .MuiInputLabel-root': { color },
  } as const;
};

/**
 * SX object for scenario-delta text (e.g. changed values in ShipCard /
 * VoyageCard).
 */
export const scenarioDeltaTextSx = (theme: Theme) =>
  ({
    color: getScenarioHighlightColor(theme),
  }) as const;

/**
 * Background tint for "created-by-scenario" containers.
 * Uses `alpha(warning.main, …)` so the tint always matches the border colour
 * family instead of the previously hard-coded orange.
 */
export const scenarioCreatedBgColor = (theme: Theme): string =>
  alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.1 : 0.12);

// ---------------------------------------------------------------------------
// DataGrid row / cell scenario highlights
// ---------------------------------------------------------------------------

/**
 * SX overrides for DataGrid rows that carry scenario-delta CSS classes.
 *
 * Spread this into the DataGrid's `sx` prop alongside other overrides:
 * ```
 * const gridSx = { …otherStyles, …scenarioDataGridRowSx };
 * ```
 */
export const scenarioDataGridRowSx = {
  // created / modified rows — warning-tinted background
  '& .MuiDataGrid-row.scenario-created, & .MuiDataGrid-row.scenario-created:hover, & .MuiDataGrid-row.scenario-created.Mui-hovered':
    {
      backgroundColor: (t: Theme) =>
        `${alpha(t.palette.warning.main, t.palette.mode === 'dark' ? 0.18 : 0.2)} !important`,
    },
  '& .MuiDataGrid-row.scenario-modified, & .MuiDataGrid-row.scenario-modified:hover, & .MuiDataGrid-row.scenario-modified.Mui-hovered':
    {
      backgroundColor: (t: Theme) =>
        `${alpha(t.palette.warning.main, t.palette.mode === 'dark' ? 0.18 : 0.2)} !important`,
    },

  // cancelled rows — error-tinted background
  '& .MuiDataGrid-row.scenario-cancelled, & .MuiDataGrid-row.scenario-cancelled:hover, & .MuiDataGrid-row.scenario-cancelled.Mui-hovered':
    {
      backgroundColor: (t: Theme) =>
        t.palette.mode === 'dark'
          ? `${t.palette.error.main}30 !important`
          : `${t.palette.error.light}80 !important`,
    },

  // Voyage side-effect rows — diagonal stripe pattern
  '& .MuiDataGrid-row.scenario-voyage-side-effect, & .MuiDataGrid-row.scenario-voyage-side-effect:hover, & .MuiDataGrid-row.scenario-voyage-side-effect.Mui-hovered':
    {
      background: (t: Theme) => {
        const color = alpha(
          t.palette.warning.main,
          t.palette.mode === 'dark' ? 0.14 : 0.25
        );
        return `repeating-linear-gradient(
          -45deg,
          transparent,
          transparent 6px,
          ${color} 6px,
          ${color} 12px
        ) !important`;
      },
    },

  // Cell-level highlight for changed scenario values
  '& .MuiDataGrid-cell.scenario-changed-cell': {
    color: (t: Theme) => getScenarioHighlightColor(t),
  },
} as const;
